import styled from '@emotion/styled';
import { Theme } from '@mui/material/styles';
import { HEADER_HEIGHT_NUMERIC, SIDE_BAR_WIDTH } from '@/layouts/MainLayout/utils/constants';
import { TBannerVariant } from './Banner';

export const BANNER_HEIGHT_NUMERIC = 50;
export const BANNER_HEIGHT = `${BANNER_HEIGHT_NUMERIC}px`;

export interface IBannerVariantStyles {
  border: string;
  content: string;
  gradient: string;
}

export const getBannerVariantStyles = (
  theme: Theme,
  variant: TBannerVariant,
): IBannerVariantStyles => {
  const { success, danger, info, warning, neutral, primary, chart, background } =
    theme.palette.customSecondary;

  const VARIANTS: Record<TBannerVariant, IBannerVariantStyles> = {
    success: {
      border: success.DEFAULT,
      content: success.numeric,
      gradient: `${success.DEFAULT}3d 0%, ${success.DEFAULT}26 45%, ${success.cardBg} 100%`,
    },
    error: {
      border: danger.DEFAULT,
      content: danger.numeric,
      gradient: `${danger.DEFAULT}3d 0%, ${danger.DEFAULT}26 45%, ${danger.cardBg} 100%`,
    },
    info: {
      border: info.DEFAULT,
      content: info.numeric,
      gradient: `${info.DEFAULT}3d 0%, ${info.DEFAULT}26 45%, ${info.cardBg} 100%`,
    },
    warning: {
      border: warning.DEFAULT,
      content: warning.numeric,
      gradient: `${warning.DEFAULT}3d 0%, ${warning.DEFAULT}26 45%, ${warning.cardBg} 100%`,
    },
    neutral: {
      border: neutral[400],
      content: neutral[100],
      gradient: `${background.raised} 0%, ${background.surface} 45%, ${background.base} 100%`,
    },
    accent: {
      border: neutral[400],
      content: neutral[100],
      gradient: `${primary.subtle}26 0%, ${chart[2]}33 45%, ${chart[4]}3d 100%`,
    },
  };

  return VARIANTS[variant];
};

export const StyledBannerSection = styled('div', {
  shouldForwardProp: property =>
    property !== 'isDesktop' && property !== 'stackIndex' && property !== 'variant',
})<{ theme?: Theme; isDesktop: boolean; stackIndex: number; variant: TBannerVariant }>`
  position: fixed;
  top: ${({ stackIndex }) => `${HEADER_HEIGHT_NUMERIC + stackIndex * BANNER_HEIGHT_NUMERIC}px`};
  left: ${({ isDesktop }) => (isDesktop ? SIDE_BAR_WIDTH : 0)};
  z-index: 1000;
  background-color: ${({ theme }) => theme.palette.customSecondary.background.base};
  border-bottom: 1px solid ${({ theme, variant }) => getBannerVariantStyles(theme, variant).border};
  width: ${({ isDesktop }) => (isDesktop ? `calc(100% - ${SIDE_BAR_WIDTH})` : '100%')};
  height: ${BANNER_HEIGHT};
  overflow-x: auto;
`;

export const StyledGradient = styled('div', {
  shouldForwardProp: property => property !== 'variant',
})<{ theme?: Theme; variant: TBannerVariant; shouldCenter: boolean }>`
  display: flex;
  justify-content: ${({ shouldCenter }) => (shouldCenter ? 'center' : 'space-between')};
  align-items: center;
  gap: 16px;
  padding: 10px 15px;
  width: 100%;
  height: 100%;
  background: ${({ theme, variant }) =>
    `linear-gradient(90deg, ${getBannerVariantStyles(theme, variant).gradient})`};
  color: ${({ theme, variant }) => getBannerVariantStyles(theme, variant).content};
`;

export const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
